/**
 * buildActivityFeed — unified activity feed engine.
 *
 * Pure function: takes raw collections from any data source,
 * normalizes them into ActivityItems, sorts by timestamp, slices to limit.
 *
 * Designed for:
 *   - District-level timeline (all activity across the district)
 *   - Entity-scoped history (activity for one person/family/church)
 *   - Future: realtime updates, infinite scroll, filter presets
 *
 * @param {Object} sources
 *   @param {Object[]} [sources.notes]    — raw Dexie notes
 *   @param {Object[]} [sources.visits]   — raw API visits
 *   @param {Object[]} [sources.prayers]  — raw API prayers
 *   @param {Object[]} [sources.events]   — raw API events (any type)
 *
 * @param {Object} [options]
 *   @param {number}  [options.limit=20]       — max items returned
 *   @param {'desc'|'asc'} [options.sortDir='desc'] — sort direction
 *   @param {string[]} [options.types]          — filter to specific activity types
 *
 * @returns {ActivityItem[]}
 */

import {
  transformNote,
  transformVisit,
  transformPrayer,
  transformEvent,
} from "../transformers/transformers";

export function buildActivityFeed(
  { notes = [], visits = [], prayers = [], events = [] } = {},
  { limit = 20, sortDir = "desc", types = null } = {}
) {
  const items = [
    ...notes
      .filter((n) => !n._deleted && !n.archived)
      .map(transformNote),
    ...visits.map(transformVisit),
    ...prayers.map(transformPrayer),
    ...events.map(transformEvent),
  ];

  const filtered = types
    ? items.filter((item) => types.includes(item.type))
    : items;

  return filtered
    .filter((item) => !!item.timestamp)
    .sort((a, b) =>
      sortDir === "desc"
        ? b.timestamp.localeCompare(a.timestamp)
        : a.timestamp.localeCompare(b.timestamp)
    )
    .slice(0, limit);
}

/**
 * Build an entity-scoped feed (activity related to one entity).
 * Convenience wrapper that pre-filters by entityId before building.
 *
 * @param {string} entityId
 * @param {Object} sources
 * @param {Object} [options]
 * @returns {ActivityItem[]}
 */
export function buildEntityFeed(entityId, sources, options) {
  const scoped = {
    notes:   (sources.notes   || []).filter((n) => n.linked_id  === entityId),
    visits:  (sources.visits  || []).filter((v) => v.church_id  === entityId),
    prayers: (sources.prayers || []).filter((p) => p.person_id  === entityId || p.family_id === entityId),
    events:  (sources.events  || []).filter((e) => e.church_id  === entityId),
  };
  return buildActivityFeed(scoped, options);
}
